import React, { useEffect, useState, useCallback } from 'react';
import {
    Grid,
    Typography,
    Box, List, ListItem, ListItemText,
    Button,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    CardHeader,
    CardContent,
    CardActions,
    MenuItem, ListItemSecondaryAction,
} from "@mui/material";
import { useDispatch } from "react-redux";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Loader, FormCard, Menu, Accordion} from "../../../../../../Components";
import { setModal } from "../../../../../Common/App";
import {
    StatusModal,
    StatusTypeModal,
    deleteStatus,
    fetchStatusTypes,
    deleteStatusType
} from '../../../index';
import {usePermissions} from "../../../../../../Hooks/usePermissions";
import {cleanName} from "../../../../../../Setup/Config/formats";

export const StatusTab = () => {

    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [menuState, setMenuState] = useState({});
    const {hasPermission, hasAnyPermission} = usePermissions();

    // Fetch data
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchStatusTypes({ dispatch, filters: { paginate: 'false', sort_by:'name', sort_dir:'asc', with:['statuses'], columns:['id','name','description','is_template']} }).then((res) => {
            if (res.success) {
                setItems(res.data || []);
            }
        });
        setIsLoading(false);
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle menu open/close
    const handleClick = (event, id) => {
        event.stopPropagation();
        setMenuState((prev) => ({
            ...prev,
            [id]: event.currentTarget, // Set the anchor element for the specific item
        }));
    };

    const handleClose = (id) => {
        setMenuState((prev) => ({
            ...prev,
            [id]: null, // Reset the anchor element for the specific item
        }));
    };

    const handleDeleteStatusType = async (e, id) => {
        e.stopPropagation();
        await deleteStatusType({ dispatch, id }).then((res) => {
            if (res.success) {
                fetchData();
            }
        });
    }

    // Handle edit
    const handleEditStatusType = (e, item) => {
        e.stopPropagation();
        dispatch(setModal({component: <StatusTypeModal item={item} setItems={setItems} />,open: true,}));
    }

    // Handle delete
    const handleDeleteStatus = async (e, id) => {
        e.stopPropagation();
        await deleteStatus({ dispatch, id }).then((res) => {
            if (res.success) {
                fetchData();
            }
        });
    };

    // Handle edit
    const handleEditStatus = (e, item) => {
        e.stopPropagation();
        dispatch(setModal({component: <StatusModal item={item} setItems={setItems} />,open: true,}));
    };

    return (
        <FormCard>
            <CardHeader
                title="Status Settings"
                avatar={<Loader isLoading={isLoading} type={"linear"} />}
            />
            <CardContent sx={{ pb: '1rem!important' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {items.map((item) => (
                            <Accordion key={item.id} rounded={0} elevation={0}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel-${item.id}-content`}
                                    id={`panel-${item.id}-header`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexDirection: 'row-reverse',
                                    }}
                                >
                                    {/* Title and Expand Icon */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                        <Typography sx={{ flexGrow: 1 }}>{`${cleanName(item.name)}`}</Typography>
                                    </Box>

                                    {/* Action Buttons */}
                                    {hasAnyPermission(['core.status.status_type.edit','core.status.status_type.delete']) && (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                aria-label="more"
                                                id={`menu-button-${item.id}`}
                                                aria-controls={menuState[item.id] ? `menu-${item.id}` : undefined}
                                                aria-expanded={menuState[item.id] ? 'true' : undefined}
                                                aria-haspopup="true"
                                                onClick={(e) => handleClick(e, item.id)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                            <Menu
                                                id={`menu-${item.id}`}
                                                anchorEl={menuState[item.id]}
                                                open={Boolean(menuState[item.id])}
                                                onClose={() => handleClose(item.id)}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                }}
                                            >
                                                {hasPermission('core.status.status_type.edit') && (
                                                    <MenuItem onClick={(e) => handleEditStatusType(e, item)}>
                                                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                                    </MenuItem>
                                                )}
                                                {hasPermission('core.status.status_type.delete') && (
                                                    <MenuItem onClick={(e) => handleDeleteStatusType(e, item.id)}>
                                                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                                                    </MenuItem>
                                                )}
                                            </Menu>
                                        </Box>
                                    )}
                                </AccordionSummary>
                                <AccordionDetails>
                                    {item?.description && <Typography variant="body2" sx={{ mb: 1 }}>{item?.description}</Typography>}
                                    <List>
                                        {item?.statuses?.map((status) => (
                                            <ListItem key={status.id}>
                                                <ListItemText primary={`${cleanName(status?.name)} - ${cleanName(status?.verb || '')}`} secondary={status?.description} />
                                                {hasAnyPermission(['core.status.edit','core.status.delete']) && (
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="more"
                                                            id={`menu-button-${status.id}`}
                                                            aria-controls={menuState[status.id] ? `menu-${status.id}` : undefined}
                                                            aria-expanded={menuState[status.id] ? 'true' : undefined}
                                                            aria-haspopup="true"
                                                            onClick={(e) => handleClick(e, status.id)}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>

                                                        <Menu
                                                            id={`menu-${status.id}`}
                                                            anchorEl={menuState[status.id]}
                                                            open={Boolean(menuState[status.id])}
                                                            onClose={() => handleClose(status.id)}
                                                            anchorOrigin={{
                                                                vertical: 'bottom',
                                                                horizontal: 'right',
                                                            }}
                                                            transformOrigin={{
                                                                vertical: 'top',
                                                                horizontal: 'right',
                                                            }}
                                                        >
                                                            {hasPermission('core.status.edit') && (
                                                                <MenuItem onClick={(e) => handleEditStatus(e, status)}>
                                                                    <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                                                </MenuItem>
                                                            )}
                                                            {hasPermission('core.status.delete') && (
                                                                <MenuItem onClick={(e) => handleDeleteStatus(e, status.id)}>
                                                                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                                                                </MenuItem>
                                                            )}
                                                        </Menu>
                                                    </ListItemSecondaryAction>
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>
                                    {hasPermission('core.status.create') && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="info"
                                            onClick={() => dispatch(setModal({component: <StatusModal type_id={item.id} setItems={setItems}/>, open: true}))}
                                        >
                                            Add Status
                                        </Button>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Grid>
                </Grid>
            </CardContent>
            {hasPermission('core.status.status_type.create') && (
                <CardActions>
                    <Button variant="contained" size="small" color="info" onClick={() => dispatch(setModal({component: <StatusTypeModal setItems={setItems} />, open: true,}))}>
                        + New Type
                    </Button>
                </CardActions>
            )}
        </FormCard>
    );
};